import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Box, Button, Tooltip } from '@components/common';
import SvgIcon from '@components/SvgIcon';
import { SubscriptionSelectors } from '@redux/SubscriptionRedux';
import useDetectSize from '@hooks/useDetectSize';
import defaultTheme from '@styles/theme';
import CycleHeader from './CycleHeader';
import {
  CalendarContainer,
  CalendarButton,
  CalendarViewFullItem
} from '@components/Calendar';

const DEFAULT_WEEK_COUNTS = 4;

const Row = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  border-bottom: 1px solid rgb(216, 216, 216);

  &:last-child {
    border-bottom: 0;
  }

  ${props =>
    props.type === 'range' && {
      background: defaultTheme.colors.secondary
    }}
`;

function CycleCalendarView(props) {
  const {
    cleansList,
    numOfRows,
    isPaused,
    isStopped,
    disabledCycleLength,
    onModify,
    onPause,
    onStop
  } = props;
  const isMobile = useDetectSize();
  if (!cleansList) {
    return null;
  }

  const enabledRows = numOfRows - disabledCycleLength;

  const rows = Array(numOfRows || DEFAULT_WEEK_COUNTS).fill(0);
  const days = Array(6).fill(0);

  const specialItems = cleansList.reduce((acc, item) => {
    const {
      week,
      day,
      serviceDate,
      serviceType,
      additionalService,
      timeframe,
      timeframeTitle
    } = item;
    if (!acc[week]) {
      acc[week] = {};
    }
    acc[week][day] = {
      date: serviceDate,
      serviceType,
      additionalService,
      timeframe,
      timeframeTitle
    };
    return acc;
  }, {});

  const renderHeader = () => {
    return (
      <CycleHeader onModify={isStopped || isPaused ? undefined : onModify} />
    );
  };

  const renderItem = (specialItem, disabled) => {
    const {
      serviceType,
      additionalService,
      timeframe,
      timeframeTitle
    } = specialItem;
    const { icon: serviceTypeIcon, fullTitle } = serviceType;

    const iconFill = disabled ? 'darkGray' : undefined;

    const iconProps = isMobile
      ? { width: 24, height: 24 }
      : { width: 16, height: 16 };

    return (
      <CalendarViewFullItem
        iconFill={iconFill}
        serviceTypeIcon={serviceTypeIcon}
        timeframeIcon={timeframe}
        timeframeTitle={timeframeTitle}
        fullTitle={fullTitle}
        extraItems={isMobile ? undefined : additionalService}
        iconProps={iconProps}
        disabled={disabled}
      />
    );
  };

  const renderRow = (zero, rowIndex) => {
    const rowKey = `subscription_row_${rowIndex}`;

    return (
      <Row key={rowKey}>
        {days.map((zero, dayIndex) => {
          const key = `cycle_btn_${dayIndex}`;
          const specialItem = specialItems[rowIndex + 1]
            ? specialItems[rowIndex + 1][dayIndex + 1]
            : null;
          return (
            <CalendarButton
              key={key}
              specialItem={specialItem}
              renderSpecialItem={renderItem}
              disabled={!specialItem || rowIndex >= enabledRows}
            />
          );
        })}
      </Row>
    );
  };

  return (
    <Box display="flex" flexDirection="row" mr={4}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        {renderHeader()}
        <CalendarContainer
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mb={4}
        >
          {rows.map(renderRow)}
        </CalendarContainer>
        <Box
          width={1}
          flexDirection="row"
          display="flex"
          justifyContent="flex-end"
        >
          {!isStopped && (
            <Button mr={4} variant="secondary" size="small" onClick={onPause}>
              {isPaused ? 'Modify Resume Date' : 'Pause services'}
            </Button>
          )}
          <Button
            variant="secondary"
            size="small"
            disabled={isStopped}
            onClick={onStop}
          >
            Stop services
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

CycleCalendarView.propTypes = {
  cleansList: PropTypes.array,
  numOfRows: PropTypes.number,
  isPaused: PropTypes.bool,
  isStopped: PropTypes.bool,
  disabledCycleLength: PropTypes.number,
  onModify: PropTypes.func,
  onPause: PropTypes.func
};

const mapStatesToProps = state => ({
  cleansList: SubscriptionSelectors.selectActiveCycles(state),
  numOfRows: SubscriptionSelectors.selectCycleLength(state),
  isPaused: SubscriptionSelectors.selectIsPaused(state),
  isStopped: SubscriptionSelectors.selectIsStopped(state),
  disabledCycleLength: SubscriptionSelectors.selectDisabledCycleLength(state)
});

export default connect(mapStatesToProps)(CycleCalendarView);
